import { Col, Row, Text } from 'components';
import { Api } from 'lib';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Header } from 'screens/Admin/components';
import FilterBar from './components/FilterBar';
import KpiCards from './components/KpiCards';

const LiveStatsEngagement = () => {
  const { t } = useTranslation();
  const [teams, setTeams] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(true);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [games, setGames] = useState([]);
  const [loadingGames, setLoadingGames] = useState(false);
  const [selectedGame, setSelectedGame] = useState(null);
  const [isAggregate, setIsAggregate] = useState(false);
  const prevTeamRef = useRef(null);
  const prevGameRef = useRef(null);
  const restoringRef = useRef(false);
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    fetchTeams();
  }, []);

  useEffect(() => {
    if (!selectedTeam) return;
    if (restoringRef.current) {
      restoringRef.current = false;
      return;
    }
    setSelectedGame(null);
    fetchGames(selectedTeam._id);
  }, [selectedTeam?._id]);

  useEffect(() => {
    fetchSummary();
  }, [selectedGame?._id, selectedTeam?._id, isAggregate, refreshTick]);

  async function fetchTeams() {
    setLoadingTeams(true);
    const res = await Api.get('/admin/getTeams?countryCode=JPN');
    const list = Array.isArray(res) ? res : [];
    setTeams(list.sort((a, b) => a.name.localeCompare(b.name)));
    setLoadingTeams(false);
  }

  async function fetchGames(teamId) {
    setLoadingGames(true);
    const res = await Api.post('/admin/games/get', { teamId, sortBy: { date: -1 }, limit: 100 });
    setGames(res?.games || []);
    setLoadingGames(false);
  }

  async function fetchSummary() {
    if (!isAggregate && !selectedGame && !selectedTeam) {
      setSummary(null);
      return;
    }

    setLoadingSummary(true);

    let res;
    if (isAggregate) {
      res = await Api.get('/admin/games/liveEngagement/aggregate');
      setSummary(res?.data ?? null);
    } else if (selectedGame) {
      res = await Api.get(`/admin/games/liveEngagement/aggregate?gameId=${selectedGame._id}`);
      setSummary(res?.data ?? null);
    } else if (selectedTeam) {
      res = await Api.get(`/admin/games/liveEngagement/aggregate?teamId=${selectedTeam._id}`);
      setSummary(res?.data ?? null);
    }

    setLoadingSummary(false);
  }

  function handleTeamSelect(team) {
    setSelectedTeam(team);
    setIsAggregate(false);
  }

  function handleAggregateToggle(val) {
    setIsAggregate(val);
    if (val) {
      prevTeamRef.current = selectedTeam;
      prevGameRef.current = selectedGame;
      setSelectedTeam(null);
      setSelectedGame(null);
      setGames([]);
    } else {
      if (prevTeamRef.current) {
        restoringRef.current = true;
        setSelectedTeam(prevTeamRef.current);
        setSelectedGame(prevGameRef.current);
        fetchGames(prevTeamRef.current._id);
      }
      prevTeamRef.current = null;
      prevGameRef.current = null;
    }
  }

  return (
    <Col noFlex wid="100%" ht="100vh" start>
      <Header>
        <Row between center wid="100%">
          <Text h3>Live Stats Engagement</Text>
          <button
            onClick={() => setRefreshTick((t) => t + 1)}
            disabled={loadingSummary}
            className="flex items-center gap-1.5 h-8 px-3 rounded-lg text-[13px] font-medium border border-[#CBD5E1] bg-white text-[#334155] hover:border-[#94A3B8] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <svg
              className={`w-3.5 h-3.5 ${loadingSummary ? 'animate-spin' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            Refresh
          </button>
        </Row>
      </Header>

      <div className="w-full mt-[54px] flex flex-col bg-[#F8FAFC] h-[calc(100vh-54px)]">
        <FilterBar
          teams={teams}
          loadingTeams={loadingTeams}
          selectedTeam={selectedTeam}
          onTeamSelect={handleTeamSelect}
          games={games}
          loadingGames={loadingGames}
          selectedGame={selectedGame}
          onGameSelect={setSelectedGame}
          isAggregate={isAggregate}
          onAggregateToggle={handleAggregateToggle}
        />

        {!isAggregate && !selectedTeam ? (
          <div className="flex items-center justify-center flex-1">
            <p className="text-[14px] text-[#94A3B8]">
              {t('admin.liveStatsEngagement.selectTeamPrompt')}
            </p>
          </div>
        ) : (
          <KpiCards summary={summary} loading={loadingSummary} />
        )}
      </div>
    </Col>
  );
};

export default LiveStatsEngagement;
